import React, { Component } from 'react';
import Button from 'react-bootstrap/Button';
import { backIcon, checkbox, checkboxSelected, calculateIcon, printIcon, restartIcon, errorIcon } from '../../../assets/images';
import { polyglot } from '../../../assets/locales';
import { formatter, CalculationUtils } from '../../../utility';
import { NormalizationConstants } from '../../../constants';
import { FullRentIncreaseBreakdown } from '../../Helper/RentIncreaseBreakdowns';
import validate from '../../../utility/validate';
import { InputBase } from '../../Helper/InputBase';
import ROUTES from '../../../constants/RouteConstants';
import moment from 'moment';

class Step2 extends Component {
  state = {
    rent: '',
    cpi: '',
    totalAmount: 0,
    calculateDisabled: false,
    errors: {},
  };

  //get cpi values from cpi JSON config
  calculateTargetYearCPI = () => {
    const { cpiConfig } = this.props;
    let cpi = '';

    try {
      cpi = CalculationUtils.getCpiForYear(cpiConfig, cpiConfig.target_year);
    } catch {
      cpi = '0';
    }

    this.setState({ cpi: cpi });
  };

  /**
   * Activated once user clicks on "calculate" button. If no errors exist, it will see which improvements have been checked,
   * calculate total eligible amount, and displays the increase breakdown
   */
  calculateEligibleAmount = () => {
    const { rent } = this.state;
    const { improvements } = this.props;

    const totalAmount = improvements.reduce((total, improvement) => {
      if (improvement.checked) return Number(total.toFixed(2)) + Number(improvement.calculatedAmount.toFixed(2));
      return total;
    }, 0);

    const validationArray = [
      {
        rent: rent,
        validationRules: [
          { minValue: 1, errorMsg: polyglot.t('errors.rent.length') },
          { isRequired: true, errorMsg: polyglot.t('errors.rent.empty') },
        ],
      },
    ];

    let err = validate(validationArray);

    if (improvements.every((improvement) => !improvement.checked)) err = { ...err, select: polyglot.t('errors.improvement.empty') };
    if (err.rent || err.select) {
      this.setState({ errors: err });
    } else {
      this.setState({ totalAmount, calculateDisabled: true, errors: err });
    }
  };

  //sets rent state to input value. If rent > maxRent, input will not be accepted.
  setRent = (event) => {
    const { value } = event.target;
    this.setState({ rent: value, calculateDisabled: false, errors: { ...this.state.errors, rent: '' } });
  };

  /**
   * updates checked property to true or false in improvements redux store
   * @param {Number} id - improvement id
   */
  toggleCheckbox = (id) => {
    const { improvements, updateImprovement } = this.props;
    const updateIndex = improvements.findIndex((improvement) => improvement.id === id);
    const updateObject = { ...improvements[updateIndex], checked: !improvements[updateIndex].checked };

    updateImprovement(updateObject.id, updateObject);
    this.setState({ calculateDisabled: false, totalAmount: 0, errors: { ...this.state.errors, select: '' } });
  };

  /**
   * Non selected items will not show up in the total, or the page print
   * @returns {html} renders improvement select checkboxes
   */
  renderImprovementSelect = () => {
    const { improvements } = this.props;

    return improvements.map((improvement) => {
      return (
        <span key={improvement.id} className={`step2-checkboxes-wrapper${improvement.checked ? '' : '-noPrint'}`}>
          <img className="step2-checkboxes-wrapper-icon" src={improvement.checked ? checkboxSelected : checkbox} alt="" onClick={() => this.toggleCheckbox(improvement.id)} />
          <span className="step2-checkboxes-wrapper-text">
            {formatter.toAmountDisplay(improvement.calculatedAmount)} - {improvement.title}
          </span>
        </span>
      );
    });
  };

  /**
   * If there is more than 1 improvement, we calculate <lowest calculated eligible amount> and <sum of all calculated
   * eligible amounts> and render multi description content. Otherwise display <calculated eligible amount>.
   * @returns {html} - description either single or multi description content
   */
  generateDescription = () => {
    const { improvements } = this.props;
    if (improvements.length < 2) {
      return <p className="step2-description-paragraph">{polyglot.t('step2.improvementCostSingle', { cost: formatter.toAmountDisplay(improvements[0].calculatedAmount) })}</p>;
    }

    const eligibleAmountSum = improvements.reduce((total, improvement) => {
      return Number(total) + Number(improvement.calculatedAmount);
    }, 0);
    const minEligibleAmount = improvements.reduce((min, el) => (Number(el.calculatedAmount) < min ? Number(el.calculatedAmount) : min), improvements[0].calculatedAmount);

    return (
      <p className="step2-description-paragraph">
        {polyglot.t('step2.improvementCostMulti', {
          lowestCost: formatter.toAmountDisplay(minEligibleAmount),
          allCosts: formatter.toAmountDisplay(eligibleAmountSum),
        })}
      </p>
    );
  };

  goToStep1 = () => {
    const { navigate, goToStep1 } = this.props;
    goToStep1();
    navigate(ROUTES.EXPLORER_STEP1);
  };

  componentDidMount() {
    this.calculateTargetYearCPI();
  }

  render() {
    const { rent, errors, cpi, calculateDisabled, totalAmount } = this.state;
    const { reset } = this.props;
    const cpiPercent = cpi.value ? cpi.value.toFixed(1) : '0.0';
    const rentLabel = polyglot.t(calculateDisabled ? 'step2.unitRentEntered' : 'step2.unitRent');
    return (
      <div className="step2">
        {/* HEADER + DESCRIPTION */}
        <div className="step2-title">{polyglot.t('header.bodyHeader')}</div>
        <div className="step1-disclaimer">{polyglot.t('header.disclaimer')}</div>
        <div className="step2-title-subtext">{polyglot.t('step2.subText')}</div>
        <div className="step2-title-print">
          <div className="step2-title-print-branch">{polyglot.t('step2.print.branch')}</div>
          <div className="step2-title-print-date">{polyglot.t('step2.print.date', { date: formatter.toDateDisplay(moment()) })}</div>
        </div>
        {/*  prettier-ignore*/}
        <div className="step2-description">
          <p className="step2-description-paragraph">
              {polyglot.t('step2.description1')} <a className="step2-description-paragraph-link" href={polyglot.t('step2.maxAllowedUrl')} target="_blank" rel="noopener noreferrer">{polyglot.t('step2.website')}</a>.
          </p>
          {this.generateDescription()}
          <p className="step2-description-paragraph">
            {polyglot.t('step2.additionalInfo.text')}
            <a className="step2-description-paragraph-link" href={polyglot.t('step2.additionalInfo.url')} target="_blank" rel="noopener noreferrer">{polyglot.t('step2.additionalInfo.urlText')}</a>.
          </p>
        </div>
        {/* INPUTS */}
        <div className="step2-print">
          <div className="step2-inputs">
            <div className="step2-inputs-wrapper">
              <InputBase
                cssClass="step2-inputs-rent"
                name="rent"
                type="number"
                maxLength={NormalizationConstants.maxRent}
                value={rent}
                errors={errors.rent}
                handleChange={this.setRent}
                startAdornment="$"
                label={rentLabel}
              />
            </div>
            <div className="step2-inputs-print">
              <span className="step2-inputs-print-value" name="print">
                ${rent}
              </span>
              <span>{rentLabel}</span>
            </div>
          </div>
          <div className="step2-inputs">
            <div className="step2-inputs-wrapper">
              <InputBase
                cssClass="step2-inputs-cpi"
                disabled
                name="cpiValue"
                type="text"
                value={cpiPercent} 
                startAdornment="%"
                label={polyglot.t('step2.cpiValue')}
              />
            </div>
            <div className="step2-inputs-print">
              <span className="step2-inputs-print-value" name="print">
                {cpi.value}%
              </span>
              <span>{polyglot.t('step2.cpiValue')}</span>
            </div>
          </div>
        </div>
        {/* IMPROVEMENT SELECT */}
        <div className="step2-select-wrapper">
          <div className="step2-select">
            <span>
              {polyglot.t('step2.improvementSelect')} &nbsp;
              <span className="step2-select-back" onClick={this.goToStep1}>
                <img className="step2-select-back-icon" src={backIcon} alt="" /> {polyglot.t('step2.editImprovements')}
              </span>
            </span>
          </div>
          <div className="step2-checkboxes">{this.renderImprovementSelect()}</div>
        </div>
        <label className={`step2-inputs-error${errors.select ? '' : '-hide'}`} style={{}}>
          {errors.select ? <img className="step2-inputs-error-icon" src={errorIcon} alt="" /> : null}
          {errors.select}
        </label>
        {/* TOTAL ELIGIBLE AMOUNT */}
        <div className="step2-total">
          <input className="step2-total-input" disabled name="cpiValue" type="text" value={formatter.toAmountDisplay(totalAmount || 0)} />
          <div className="step2-inputs-print">
            <span className="step2-inputs-print-value" name="print">
              {formatter.toAmountDisplay(totalAmount)}
            </span>
            <span>{polyglot.t('step2.eligibleAmount')}</span>
          </div>
        </div>
        {/* BUTTONS */}
        <div className="step2-buttons">
          <Button variant="success" className="step2-buttons-calculate" disabled={calculateDisabled} onClick={this.calculateEligibleAmount}>
            <img className="step2-buttons-icon" src={calculateIcon} alt="" /> Calculate
          </Button>
          {calculateDisabled ? (
            <div className="step2-buttons-wrapper">
              <Button variant="noColor" className="step2-buttons-noColor" onClick={() => window.print()}>
                <img className="step2-buttons-icon" src={printIcon} alt="" /> Print
              </Button>
              <Button variant="noColor" className="step2-buttons-noColor" onClick={() => reset()}>
                <img className="step2-buttons-icon" src={restartIcon} alt="" /> Start Over
              </Button>
            </div>
          ) : null}
        </div>
        {calculateDisabled ? <FullRentIncreaseBreakdown cpi={cpi} rent={rent} eligibleAmount={totalAmount} /> : null}
      </div>
    );
  }
}

export default Step2;